import { BaseService } from '../base/BaseService';

export interface VideoJobRow {
  id: string;
  prompt: string | null;
  model: string | null;
  duration_seconds: number | null;
  resolution: string | null;
  source_image_url: string | null;
  seed: number | null;
}

/** The progress fields, kept separate because the reprise read does not need them. */
export interface VideoJobStatusRow {
  id: string;
  status: string;
  video_url: string | null;
  cover_image_url: string | null;
  error_message: string | null;
  model: string | null;
  resolution: string | null;
  duration_seconds: number | null;
}

class VideoJobService extends BaseService {
  /**
   * `video_jobs` is account-scoped (`src/lib/identity/table-scope.generated.ts:104`),
   * so both reads below stamp `ensureAuthenticatedOwner()`. This method used
   * `ensureAuthenticated()` — the raw session id — until 2026-08-08, which is
   * the identity invariant's signature failure: for an account OWNER the two
   * ids are identical, so the bug is invisible in dev, in review and to the
   * whole vitest suite (which mocks Supabase and never evaluates a policy).
   * For a member of a shared account it returns zero rows and the video simply
   * appears not to exist.
   */
  async getVideoJobById(id: string): Promise<VideoJobRow | null> {
    if (!id) return null;
    const ownerId = await this.ensureAuthenticatedOwner();
    const { data, error } = await this.client
      .from('video_jobs')
      .select('id, prompt, model, duration_seconds, resolution, source_image_url, seed')
      .eq('id', id)
      .eq('user_id', ownerId)
      .maybeSingle();
    if (error) throw error;
    return (data as VideoJobRow | null) ?? null;
  }

  /** Polled by the `video` job kind while its run is live. */
  async getVideoJobStatus(id: string): Promise<VideoJobStatusRow | null> {
    if (!id) return null;
    const ownerId = await this.ensureAuthenticatedOwner();
    const { data, error } = await this.client
      .from('video_jobs')
      .select(
        'id, status, video_url, cover_image_url, error_message, model, resolution, duration_seconds',
      )
      .eq('id', id)
      .eq('user_id', ownerId)
      .maybeSingle();
    if (error) throw error;
    return (data as VideoJobStatusRow | null) ?? null;
  }
}

export const videoJobService = new VideoJobService();
